import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function DisclaimerPage() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = 'Disclaimer | Kailac';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Important disclaimer for Kailac advisory services. Not a lender. Results not guaranteed. Subject to eligibility and lender requirements.');
  }, []);

  return (
    <div className="min-h-screen bg-[#FCFAFD] pb-16 lg:pb-0">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 lg:px-8 pt-32 pb-20">
        <div className="mb-10">
          <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-3">{t('legal_eyebrow')}</p>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">{t('legal_disclaimer_title')}</h1>
          <p className="text-sm text-gray-400">{t('legal_last_updated')}: January 1, 2024</p>
        </div>

        {/* Prominent Notice */}
        <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-7 mb-10">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-100 flex-shrink-0">
              <i className="ri-error-warning-line text-amber-700 text-xl"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-amber-900 mb-2">{t('legal_notice_title')}</h2>
              <p className="text-sm text-amber-800 leading-relaxed">
                Kailac is not a lender, financial institution, or broker. We provide advisory guidance services only. <strong>Approval for any funding program is not guaranteed.</strong> All results are subject to individual eligibility and lender/program requirements.
              </p>
            </div>
          </div>
        </div>

        <div className="prose max-w-none text-gray-600 leading-relaxed space-y-8">
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">Not a Lender</h2>
            <p>Kailac is an advisory services company. We are not a bank, lender, credit union, or financial institution. We do not issue loans, extend credit, or make lending decisions. Any funding accessed by clients is provided by independent third-party lenders and program providers based on their own criteria and approval processes.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">No Guarantee of Approval or Results</h2>
            <p>Kailac does not guarantee that any client will be approved for any funding program, receive any specific amount of funding, or achieve any particular outcome. All funding decisions are made solely by the relevant lenders and program providers. Eligibility requirements, approval criteria, and available amounts vary by program and individual applicant profile.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">Individual Results May Vary</h2>
            <p>Any testimonials, case studies, or results described on our website represent individual experiences of specific clients and are not typical results. Results vary significantly based on individual credit profiles, business history, financial situation, lender requirements, and other factors. Past results are not indicative of future outcomes.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">0% Interest — Program Terms</h2>
            <p>References to &ldquo;0% interest&rdquo; or &ldquo;0% APR&rdquo; refer to promotional introductory periods offered by certain credit programs. These promotional rates are time-limited (typically 12 months) and standard rates apply after the promotional period ends. Terms, availability, and rates are determined by the lender or program, not by Kailac, and are subject to change.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">Not Financial or Legal Advice</h2>
            <p>Content provided by Kailac — including on our website, blog, and in advisor communications — is for informational purposes only and does not constitute financial, legal, tax, or investment advice. You should consult with a licensed financial advisor, attorney, or tax professional before making any significant financial decision.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">Compliance Statement</h2>
            <p>Kailac is committed to operating with transparency and integrity. We use compliant, non-deceptive language in all marketing and client communications. We do not make guarantees of approval, promise specific outcomes, or use deceptive claims. Our team follows responsible advisory practices at all times.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">Contact</h2>
            <p>For questions about this disclaimer: <a href="mailto:compliance@kailac.com" className="text-[#4F2D63] hover:underline cursor-pointer">compliance@kailac.com</a></p>
          </section>
        </div>

        <div className="mt-12 flex flex-wrap gap-4">
          <Link to="/privacy-policy" className="text-sm text-[#4F2D63] font-semibold hover:underline cursor-pointer">{t('legal_nav_privacy')}</Link>
          <Link to="/terms-of-service" className="text-sm text-[#4F2D63] font-semibold hover:underline cursor-pointer">{t('legal_nav_terms')}</Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
